import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Users, Mail, Sparkles, RotateCcw, Send, Loader2, Copy, CheckCircle2, AlertCircle, Image as ImageIcon, X } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/hooks/useFetch';

/**
 * BulkUsersTab — Lane D
 * Two sub-tabs:
 *   1. Bulk create users (email + name CSV) with optional welcome email
 *   2. Welcome-email template editor (subject + HTML, with preview / send-test / reset)
 */
const PLACEHOLDERS = [
  { token: '{{first_name}}', desc: "Recipient's first name" },
  { token: '{{name}}', desc: 'Full name' },
  { token: '{{email}}', desc: 'Their email address' },
  { token: '{{login_url}}', desc: 'Direct login link' },
  { token: '{{temp_password_block}}', desc: 'Renders temp password box (if set)' },
  { token: '{{header_image_block}}', desc: 'Renders the uploaded header image (if set)' },
  { token: '{{custom_message_block}}', desc: 'Renders the batch note (if set)' },
];

const parseBulkInput = (raw) => {
  // Accept lines like "email, name" or just "email" — split on the FIRST comma or tab.
  return raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const idx = line.search(/[,\t]/);
      if (idx === -1) return { email: line.toLowerCase(), name: '' };
      const email = line.slice(0, idx).trim().toLowerCase();
      const name = line.slice(idx + 1).trim();
      return { email, name };
    });
};

const BulkUsersTab = () => {
  // ---------- Bulk create ----------
  const [bulkRaw, setBulkRaw] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [sendEmails, setSendEmails] = useState(true);
  const [includePassword, setIncludePassword] = useState(true);
  const [isGifted, setIsGifted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);

  const parsed = parseBulkInput(bulkRaw);
  const invalidCount = parsed.filter((p) => !p.email.includes('@')).length;

  const submit = async () => {
    if (parsed.length === 0) return toast.error('Add at least one email');
    if (invalidCount > 0) return toast.error(`${invalidCount} row(s) missing a valid email`);
    if (parsed.length > 200) return toast.error('Max 200 users per upload');

    setSubmitting(true);
    setResults(null);
    const { data, error } = await apiFetch('/api/admin/users/bulk', {
      method: 'POST',
      body: {
        users: parsed.map((p) => ({ email: p.email, name: p.name, is_gifted: isGifted })),
        send_welcome_email: sendEmails,
        include_password_in_email: includePassword,
        custom_message: customMessage || null,
      },
    });
    setSubmitting(false);
    if (error) return toast.error(error);
    setResults(data);
    toast.success(`Created ${data.summary.created} • Skipped ${data.summary.skipped} • Errors ${data.summary.error}`);
  };

  const copyCsv = () => {
    if (!results) return;
    const rows = [['email', 'status', 'temporary_password', 'reason']];
    for (const r of results.results) {
      rows.push([r.email, r.status, r.temporary_password || '', r.reason || '']);
    }
    const csv = rows.map((r) => r.map((c) => `"${(c || '').replace(/"/g, '""')}"`).join(',')).join('\n');
    navigator.clipboard.writeText(csv).then(
      () => toast.success('CSV copied to clipboard'),
      () => toast.error('Copy failed')
    );
  };

  // ---------- Template editor ----------
  const [tpl, setTpl] = useState(null);
  const [tplLoading, setTplLoading] = useState(true);
  const [tplSaving, setTplSaving] = useState(false);
  const [tplSubject, setTplSubject] = useState('');
  const [tplHtml, setTplHtml] = useState('');
  const [testTo, setTestTo] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [uploadingHeader, setUploadingHeader] = useState(false);
  const headerFileRef = React.useRef(null);

  const API = process.env.REACT_APP_BACKEND_URL;

  const loadTemplate = useCallback(async () => {
    setTplLoading(true);
    const { data, error } = await apiFetch('/api/admin/email-templates/welcome');
    setTplLoading(false);
    if (error) return toast.error(error);
    setTpl(data);
    setTplSubject(data.subject || '');
    setTplHtml(data.html || '');
  }, []);

  useEffect(() => { loadTemplate(); }, [loadTemplate]);

  const saveTemplate = async () => {
    if (tplSubject.trim().length < 3) return toast.error('Subject must be 3+ chars');
    if (tplHtml.trim().length < 30) return toast.error('HTML must be 30+ chars');
    setTplSaving(true);
    const { error } = await apiFetch('/api/admin/email-templates/welcome', {
      method: 'PUT',
      body: { subject: tplSubject, html: tplHtml },
    });
    setTplSaving(false);
    if (error) return toast.error(error);
    toast.success('Template saved');
    loadTemplate();
  };

  const resetTemplate = async () => {
    if (!window.confirm('Reset to the built-in default template? Your custom edits will be lost.')) return;
    const { error } = await apiFetch('/api/admin/email-templates/welcome/reset', { method: 'POST' });
    if (error) return toast.error(error);
    toast.success('Template reset to default');
    loadTemplate();
  };

  const sendTest = async () => {
    setSendingTest(true);
    const { data, error } = await apiFetch('/api/admin/email-templates/welcome/send-test', {
      method: 'POST',
      body: { to: testTo || null, custom_message: customMessage || null },
    });
    setSendingTest(false);
    if (error) return toast.error(error);
    toast.success(`Test sent to ${data.to}`);
  };

  const uploadHeaderImage = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) return toast.error('Image only');
    if (file.size > 3 * 1024 * 1024) return toast.error('Max 3 MB');
    setUploadingHeader(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API}/api/admin/email-templates/welcome/header-image`, {
        method: 'POST', body: fd, credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || 'Upload failed');
      toast.success('Header image uploaded');
      loadTemplate();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUploadingHeader(false);
    }
  };

  const clearHeaderImage = async () => {
    const { error } = await apiFetch('/api/admin/email-templates/welcome/header-image', { method: 'DELETE' });
    if (error) return toast.error(error);
    toast.success('Header image cleared');
    loadTemplate();
  };

  return (
    <div className="space-y-6" data-testid="bulk-users-tab">
      <Tabs defaultValue="bulk" className="w-full">
        <TabsList className="grid grid-cols-2 w-full max-w-md">
          <TabsTrigger value="bulk" data-testid="subtab-bulk-create">
            <Users className="h-4 w-4 mr-2" /> Bulk Create
          </TabsTrigger>
          <TabsTrigger value="template" data-testid="subtab-email-template">
            <Mail className="h-4 w-4 mr-2" /> Welcome Email
          </TabsTrigger>
        </TabsList>

        {/* ---------------- Bulk Create ---------------- */}
        <TabsContent value="bulk" className="mt-4 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Bulk-create users</CardTitle>
              <CardDescription>
                One per line as <code className="text-xs bg-slate-100 px-1 rounded">email, full name</code>.
                Passwords are auto-generated. Max 200 per upload. Duplicates are skipped.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="bulk-input" className="text-sm font-medium">User list</Label>
                <Textarea
                  id="bulk-input"
                  data-testid="bulk-users-input"
                  rows={8}
                  className="font-mono text-sm mt-1"
                  placeholder={'jane@example.com, Jane Doe\njohn@example.com, John Smith'}
                  value={bulkRaw}
                  onChange={(e) => setBulkRaw(e.target.value)}
                />
                <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-600">
                  <Badge variant="outline" data-testid="bulk-parsed-count">{parsed.length} row(s)</Badge>
                  {invalidCount > 0 && (
                    <Badge variant="destructive">{invalidCount} invalid</Badge>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="custom-msg" className="text-sm font-medium">
                  Custom message (optional, appears in welcome email)
                </Label>
                <Textarea
                  id="custom-msg"
                  data-testid="bulk-custom-message"
                  rows={3}
                  className="mt-1"
                  placeholder="Hi! You were invited as part of our March cohort — welcome!"
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                />
              </div>

              <div className="flex flex-wrap items-center gap-4 text-sm">
                <label className="flex items-center gap-2">
                  <Checkbox
                    checked={sendEmails}
                    onCheckedChange={setSendEmails}
                    data-testid="bulk-send-email"
                  />
                  Send welcome email
                </label>
                <label className="flex items-center gap-2">
                  <Checkbox
                    checked={includePassword}
                    onCheckedChange={setIncludePassword}
                    disabled={!sendEmails}
                    data-testid="bulk-include-password"
                  />
                  Include temp password in email
                </label>
                <label className="flex items-center gap-2">
                  <Checkbox
                    checked={isGifted}
                    onCheckedChange={setIsGifted}
                    data-testid="bulk-is-gifted"
                  />
                  Grant free VIP
                </label>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={submit}
                  disabled={submitting || parsed.length === 0}
                  data-testid="bulk-submit-btn"
                  className="bg-[#00E5CC] text-[#0d1f2d] hover:bg-[#00d4bd]"
                >
                  {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                  Create {parsed.length || ''} user{parsed.length === 1 ? '' : 's'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {results && (
            <Card data-testid="bulk-results-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Results</CardTitle>
                  <CardDescription>
                    <span className="text-emerald-600 font-medium">{results.summary.created} created</span>
                    {' • '}
                    <span className="text-amber-600">{results.summary.skipped} skipped</span>
                    {' • '}
                    <span className="text-red-600">{results.summary.error} error</span>
                  </CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={copyCsv} data-testid="bulk-copy-csv">
                  <Copy className="h-4 w-4 mr-2" /> Copy CSV
                </Button>
              </CardHeader>
              <CardContent>
                <div className="max-h-96 overflow-auto border rounded">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 dark:bg-slate-800 sticky top-0">
                      <tr>
                        <th className="text-left p-2">Email</th>
                        <th className="text-left p-2">Status</th>
                        <th className="text-left p-2">Temp Password / Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.results.map((r, i) => (
                        <tr key={r.email || `bulk-row-${i}`} className="border-t" data-testid={`bulk-row-${i}`}>
                          <td className="p-2 font-mono text-xs">{r.email}</td>
                          <td className="p-2">
                            {r.status === 'created' && <Badge className="bg-emerald-100 text-emerald-800"><CheckCircle2 className="h-3 w-3 mr-1" />created</Badge>}
                            {r.status === 'skipped' && <Badge variant="secondary">skipped</Badge>}
                            {r.status === 'error' && <Badge variant="destructive"><AlertCircle className="h-3 w-3 mr-1" />error</Badge>}
                          </td>
                          <td className="p-2 font-mono text-xs text-muted-foreground">
                            {r.temporary_password || r.reason || ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ---------------- Email Template ---------------- */}
        <TabsContent value="template" className="mt-4 space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-start justify-between">
              <div>
                <CardTitle>Welcome email template</CardTitle>
                <CardDescription>
                  {tpl?.is_custom
                    ? <>Using your custom override {tpl.updated_at && <>(last edited {new Date(tpl.updated_at).toLocaleString()})</>}</>
                    : 'Using built-in default. Edit & save to override.'}
                </CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={resetTemplate} disabled={!tpl?.is_custom} data-testid="tpl-reset-btn">
                <RotateCcw className="h-4 w-4 mr-2" /> Reset to default
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {tplLoading ? (
                <div className="flex items-center text-muted-foreground"><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading template…</div>
              ) : (
                <>
                  <div className="border rounded p-3 bg-slate-50 dark:bg-slate-800/60 dark:border-slate-700 space-y-2">
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <ImageIcon className="h-4 w-4" /> Header image (optional)
                    </Label>
                    {tpl?.header_image_url ? (
                      <div className="flex items-center gap-3">
                        <img
                          src={`${tpl.header_image_url}?t=${Date.now()}`}
                          alt="header"
                          className="max-h-20 rounded border bg-white"
                          data-testid="tpl-header-preview"
                        />
                        <div className="flex flex-col gap-1">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => headerFileRef.current?.click()}
                            disabled={uploadingHeader}
                            data-testid="tpl-header-replace"
                          >
                            {uploadingHeader ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
                            Replace
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={clearHeaderImage}
                            data-testid="tpl-header-clear"
                          >
                            <X className="h-3 w-3 mr-1" /> Remove
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => headerFileRef.current?.click()}
                        disabled={uploadingHeader}
                        data-testid="tpl-header-upload"
                      >
                        {uploadingHeader ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <ImageIcon className="h-3 w-3 mr-1" />}
                        Upload header image
                      </Button>
                    )}
                    <input
                      ref={headerFileRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif"
                      className="hidden"
                      onChange={(e) => uploadHeaderImage(e.target.files?.[0])}
                      data-testid="tpl-header-file-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Auto-prepended at the top of the email when present. Or insert <code className="bg-white dark:bg-slate-900 dark:text-slate-100 px-1 rounded">{`{{header_image_block}}`}</code> anywhere in the HTML.
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="tpl-subject" className="text-sm font-medium">Subject</Label>
                    <Input
                      id="tpl-subject"
                      data-testid="tpl-subject-input"
                      value={tplSubject}
                      onChange={(e) => setTplSubject(e.target.value)}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="tpl-html" className="text-sm font-medium">HTML body</Label>
                    <Textarea
                      id="tpl-html"
                      data-testid="tpl-html-input"
                      rows={14}
                      className="font-mono text-xs mt-1"
                      value={tplHtml}
                      onChange={(e) => setTplHtml(e.target.value)}
                    />
                  </div>

                  <details className="text-sm">
                    <summary className="cursor-pointer text-foreground font-medium">Available placeholders</summary>
                    <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                      {PLACEHOLDERS.map((p) => (
                        <li key={p.token}>
                          <code className="bg-slate-100 dark:bg-slate-800 dark:text-slate-100 px-1 rounded">{p.token}</code> — {p.desc}
                        </li>
                      ))}
                    </ul>
                  </details>

                  <div className="flex flex-wrap gap-2 pt-2">
                    <Button
                      onClick={saveTemplate}
                      disabled={tplSaving}
                      data-testid="tpl-save-btn"
                      className="bg-[#00E5CC] text-[#0d1f2d] hover:bg-[#00d4bd]"
                    >
                      {tplSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                      Save template
                    </Button>

                    <div className="flex items-center gap-2 flex-1 min-w-[260px]">
                      <Input
                        placeholder="test@email.com (defaults to yours)"
                        value={testTo}
                        onChange={(e) => setTestTo(e.target.value)}
                        data-testid="tpl-test-to-input"
                      />
                      <Button
                        variant="outline"
                        onClick={sendTest}
                        disabled={sendingTest}
                        data-testid="tpl-send-test-btn"
                      >
                        {sendingTest ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                        Send test
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Live preview */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Preview</CardTitle>
              <CardDescription>Placeholders render with sample data.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-md p-1 bg-slate-200 dark:bg-slate-700 ring-1 ring-slate-300 dark:ring-slate-600">
                <p className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-300 px-2 py-1">
                  Live email preview — recipients see exactly this
                </p>
                <div
                  className="border rounded p-4 bg-white max-h-[500px] overflow-auto"
                  data-testid="tpl-preview"
                  dangerouslySetInnerHTML={{
                    __html: tplHtml
                      .replaceAll('{{first_name}}', 'Jane')
                      .replaceAll('{{name}}', 'Jane Doe')
                      .replaceAll('{{email}}', 'jane@example.com')
                      .replaceAll('{{login_url}}', 'https://clearcommunicationapp.com/login')
                      .replaceAll(
                        '{{temp_password_block}}',
                        '<p style="font-size:15px;line-height:1.6;background:#f4f6f8;border-left:3px solid #00E5CC;padding:10px 14px;border-radius:6px;margin:16px 0"><strong>Your temporary password:</strong> <code style="font-family:monospace;background:#fff;padding:2px 6px;border-radius:4px">SamplePass123</code><br/><span style="font-size:13px;color:#666">Please change it after your first login.</span></p>'
                      )
                      .replaceAll(
                        '{{custom_message_block}}',
                        customMessage
                          ? `<div style="font-size:15px;line-height:1.6;background:#fffbe6;border-left:3px solid #f59e0b;padding:12px 16px;border-radius:6px;margin:16px 0;white-space:pre-wrap">${customMessage.replace(/</g, '&lt;')}</div>`
                          : ''
                      ),
                  }}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BulkUsersTab;
